import { UACItem } from '../constants/UACConstants';

export class UACHelper {
  private uacPermissions: UACItem[] = [];

  constructor(uacPermissions: UACItem[] = []) {
    this.uacPermissions = uacPermissions;
  }

  /**
   * Check if user has access to a specific UAC key
   * @param key - UAC key to check
   * @param permission - Permission type (VIEW or EDIT)
   * @returns boolean
   */
  hasAccess(key: string, permission: 'VIEW' | 'EDIT' = 'VIEW'): boolean {
    if (!Array.isArray(this.uacPermissions) || this.uacPermissions.length === 0) {
      return false; // Default-deny when permissions are missing/loading/malformed
    }

    const uacItem = this.uacPermissions.find((item) => item.key === key);
    return uacItem ? uacItem.permissions.includes(permission) : false;
  }

  /**
   * Check if user can edit (has EDIT permission)
   */
  canEdit(key: string): boolean {
    return this.hasAccess(key, 'EDIT');
  }

  /**
   * Check if user can view (has VIEW permission)
   */
  canView(key: string): boolean {
    return this.hasAccess(key, 'VIEW');
  }

  /**
   * Get accessible tabs from a list of tab keys
   * @param tabKeys - Array of UAC keys for tabs
   * @returns Array of accessible tab keys
   */
  getAccessibleTabs(tabKeys: string[]): string[] {
    return tabKeys.filter((key) => this.hasAccess(key));
  }

  /**
   * Get all permissions for a specific key
   */
  getPermissions(key: string): string[] {
    const uacItem = this.uacPermissions.find((item) => item.key === key);
    return uacItem?.permissions || [];
  }

  /**
   * Check if user has access to any of the provided keys
   */
  hasAccessToAny(keys: string[], permission: 'VIEW' | 'EDIT' = 'VIEW'): boolean {
    return keys.some((key) => this.hasAccess(key, permission));
  }

  /**
   * Check if user has access to all of the provided keys
   */
  hasAccessToAll(keys: string[], permission: 'VIEW' | 'EDIT' = 'VIEW'): boolean {
    return keys.every((key) => this.hasAccess(key, permission));
  }

  /**
   * Get UAC items by type
   */
  getItemsByType(type: 'MENU' | 'SUBMENU' | 'TABS' | 'SECTION' | 'BUTTON'): UACItem[] {
    return this.uacPermissions.filter((item) => item.type === type);
  }

  /**
   * Check if a UAC item is a section
   */
  isSection(item: UACItem): boolean {
    return item?.type === 'SECTION';
  }

  /**
   * Get all section items
   */
  getSections(): UACItem[] {
    return this.getItemsByType('SECTION');
  }

  /**
   * Check if user has access to a specific section
   */
  hasSectionAccess(sectionKey: string, permission: 'VIEW' | 'EDIT' = 'VIEW'): boolean {
    const section = this.uacPermissions.find(
      (item) => item.key === sectionKey && item.type === 'SECTION'
    );
    return section ? section.permissions.includes(permission) : false;
  }

  /**
   * Check if user has access to a specific button
   */
  hasButtonAccess(buttonKey: string, permission: 'CLICK' | 'EXECUTE' = 'CLICK'): boolean {
    const button = this.uacPermissions.find(
      (item) => item.key === buttonKey && item.type === 'BUTTON'
    );
    return button ? button.permissions.includes(permission) : false;
  }

  /**
   * Get all button items
   */
  getButtons(): UACItem[] {
    return this.getItemsByType('BUTTON');
  }
  /**
   * Get sections by parent key
   */
  getSectionsByParent(parentKey: string): UACItem[] {
    return this.uacPermissions.filter(
      (item) => item.type === 'SECTION' && item.parentKey === parentKey
    );
  }

  /**
   * Get UAC items by parent key
   */
  getItemsByParent(parentKey: string): UACItem[] {
    return this.uacPermissions.filter((item) => item.parentKey === parentKey);
  }
}
